import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  ChevronLeft, Smartphone, Monitor, CheckCircle2,
  AlertTriangle, ChevronDown, ChevronUp
} from 'lucide-react'
import { useApi } from '../../hooks/useApi'

// ── Types ──────────────────────────────────────────────

interface PerfPageData {
  id: number
  url: string
  pageType: string
  perfScoreMobile: number | null
  perfScoreDesktop: number | null
  seoScore: number | null
  bpScore: number | null
  lcpMs: number | null
  fcpMs: number | null
  clsValue: number | null
  ttfbMs: number | null
  inpMs: number | null
  siMs: number | null
  lcpElement: string | null
  items: PerfItem[]
}

interface PerfItem {
  id: number
  strategy: 'mobile' | 'desktop'
  category: string
  auditKey: string
  title: string
  description: string
  score: number | null
  displayValue: string | null
  result: 'passed' | 'opportunity' | 'diagnostic' | 'informative'
  savingsMs: number | null
  savingsBytes: number | null
  detailsJson: unknown
}

interface DetailItem {
  url?: string
  totalBytes?: number
  wastedBytes?: number
  wastedMs?: number
  label?: string
  [key: string]: unknown
}

// ── Helpers ────────────────────────────────────────────

function scoreColor(score: number): string {
  if (score >= 90) return '#10B981'
  if (score >= 50) return '#FBBF24'
  return '#EF4444'
}

function scoreBgClass(score: number): string {
  if (score >= 90) return 'bg-emerald-50 border-emerald-200 text-emerald-700'
  if (score >= 50) return 'bg-amber-50 border-amber-200 text-amber-700'
  return 'bg-red-50 border-red-200 text-red-700'
}

function cwvColor(value: number, goodThreshold: number, poorThreshold: number): string {
  if (value < goodThreshold) return '#10B981'
  if (value < poorThreshold) return '#F59E0B'
  return '#EF4444'
}

function cwvLabel(value: number, goodThreshold: number, poorThreshold: number): string {
  if (value < goodThreshold) return 'Bon'
  if (value < poorThreshold) return 'A ameliorer'
  return 'Mauvais'
}

function formatMs(ms: number): string {
  if (ms >= 1000) return (ms / 1000).toFixed(1) + ' s'
  return Math.round(ms) + ' ms'
}

function formatBytes(bytes: number): string {
  if (bytes >= 1048576) return (bytes / 1048576).toFixed(1) + ' MB'
  if (bytes >= 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return bytes + ' B'
}

// ── Score Card ─────────────────────────────────────────

function ScoreCard({ label, score, icon }: { label: string; score: number | null; icon: React.ReactNode }) {
  const s = score ?? 0
  const color = scoreColor(s)
  return (
    <div className="bg-white border border-[#E2E4ED] rounded-xl p-5">
      <div className="flex items-start justify-between mb-3">
        <span className="text-[13px] text-[#8B8FA7] font-medium">{label}</span>
        <div className="w-8 h-8 rounded-lg bg-[#F4F5FA] flex items-center justify-center">
          {icon}
        </div>
      </div>
      <p className="text-3xl font-bold font-mono leading-none" style={{ color }}>
        {score !== null ? Math.round(score) : '—'}
      </p>
      <span className="text-xs text-[#8B8FA7]">/100</span>
    </div>
  )
}

// ── CWV Card ───────────────────────────────────────────

function CwvCard({ label, hint, value, unit, goodThreshold, poorThreshold }: {
  label: string
  hint: string
  value: number | null
  unit: string
  goodThreshold: number
  poorThreshold: number
}) {
  if (value === null) return null
  const color = cwvColor(value, goodThreshold, poorThreshold)
  const status = cwvLabel(value, goodThreshold, poorThreshold)
  const displayVal = unit === 's' ? (value / 1000).toFixed(2) : value.toFixed(unit === '' ? 3 : 0)
  const displayUnit = unit === 's' ? 's' : unit === '' ? '' : ' ms'

  return (
    <div className="bg-white border border-[#E2E4ED] rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-[#555A73] font-medium uppercase tracking-wide">{label}</span>
        <span
          className="text-[10px] font-medium px-2 py-0.5 rounded-full"
          style={{ backgroundColor: color + '15', color }}
        >
          {status}
        </span>
      </div>
      <div className="flex items-baseline gap-1">
        <span className="font-mono text-2xl font-bold" style={{ color }}>{displayVal}</span>
        <span className="text-xs text-[#555A73]">{displayUnit}</span>
      </div>
      <span className="text-[11px] text-[#8B8FA7]">{hint}</span>
    </div>
  )
}

// ── Audit Item Card ────────────────────────────────────

function AuditItemCard({ item }: { item: PerfItem }) {
  const [expanded, setExpanded] = useState(false)

  const scoreDisplay = item.score !== null ? Math.round(item.score * 100) : null
  const hasDetails = item.detailsJson && Array.isArray((item.detailsJson as { items?: unknown[] })?.items)
  const detailItems: DetailItem[] = hasDetails
    ? ((item.detailsJson as { items: DetailItem[] }).items ?? []).slice(0, 10)
    : []

  return (
    <div className="bg-white border border-[#E2E4ED] rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-[#F9FAFB] transition-colors"
      >
        {/* Score gauge */}
        {scoreDisplay !== null && (
          <div
            className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-mono font-bold border shrink-0 ${scoreBgClass(scoreDisplay)}`}
          >
            {scoreDisplay}
          </div>
        )}
        {scoreDisplay === null && (
          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#F4F5FA] border border-[#E2E4ED] shrink-0">
            <span className="text-xs text-[#8B8FA7]">—</span>
          </div>
        )}

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#1B2559] truncate">{item.title}</p>
          <div className="flex items-center gap-3 mt-0.5">
            {item.displayValue && (
              <span className="text-xs text-[#8B8FA7]">{item.displayValue}</span>
            )}
            {item.savingsMs !== null && item.savingsMs > 0 && (
              <span className="text-xs font-mono text-amber-600">-{formatMs(item.savingsMs)}</span>
            )}
            {item.savingsBytes !== null && item.savingsBytes > 0 && (
              <span className="text-xs font-mono text-amber-600">-{formatBytes(item.savingsBytes)}</span>
            )}
          </div>
        </div>

        {(detailItems.length > 0 || item.description) && (
          expanded
            ? <ChevronUp className="w-4 h-4 text-[#8B8FA7] shrink-0" strokeWidth={1.25} />
            : <ChevronDown className="w-4 h-4 text-[#8B8FA7] shrink-0" strokeWidth={1.25} />
        )}
      </button>

      {expanded && (
        <div className="px-5 pb-4 border-t border-[#E2E4ED] pt-3">
          {item.description && (
            <p className="text-xs text-[#555A73] mb-3">{item.description}</p>
          )}
          {detailItems.length > 0 && (
            <div className="space-y-1.5">
              {detailItems.map((d, i) => (
                <div key={i} className="flex items-center gap-3 text-xs bg-[#F9FAFB] rounded-lg px-3 py-2">
                  <span className="text-[#555A73] truncate flex-1">
                    {d.url || d.label || JSON.stringify(d).slice(0, 80)}
                  </span>
                  {d.totalBytes != null && (
                    <span className="text-[#8B8FA7] shrink-0">{formatBytes(d.totalBytes)}</span>
                  )}
                  {d.wastedBytes != null && (
                    <span className="text-amber-600 font-mono shrink-0">-{formatBytes(d.wastedBytes)}</span>
                  )}
                  {d.wastedMs != null && (
                    <span className="text-amber-600 font-mono shrink-0">-{formatMs(d.wastedMs)}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Main Component ─────────────────────────────────────

export default function PerfPageDetail() {
  const { projectId, pageId } = useParams<{ projectId: string; pageId: string }>()
  const [strategy, setStrategy] = useState<'mobile' | 'desktop'>('mobile')
  const [showPassed, setShowPassed] = useState(false)

  const { data: page, loading, error } = useApi<PerfPageData>(
    `/api/perf-audits/0/pages/${pageId}`
  )

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-white border border-[#E2E4ED] rounded-xl p-6 animate-pulse">
            <div className="h-5 bg-[#F4F5FA] rounded w-1/3 mb-3" />
            <div className="h-4 bg-[#F4F5FA] rounded w-2/3" />
          </div>
        ))}
      </div>
    )
  }

  if (error || !page) {
    return (
      <div className="p-6">
        <div className="bg-white border border-red-200 rounded-xl p-8 text-center">
          <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" strokeWidth={1.25} />
          <p className="text-sm text-[#555A73]">{error ?? 'Page introuvable'}</p>
        </div>
      </div>
    )
  }

  const items = Array.isArray(page.items) ? page.items : []
  const filteredItems = items.filter(item => item.strategy === strategy)
  const opportunities = filteredItems
    .filter(item => item.result === 'opportunity')
    .sort((a, b) => (b.savingsMs ?? 0) - (a.savingsMs ?? 0))
  const diagnostics = filteredItems.filter(item => item.result === 'diagnostic')
  const passed = filteredItems.filter(item => item.result === 'passed')

  return (
    <div className="p-6 space-y-6">
      {/* Back link */}
      <Link
        to={`/p/${projectId}/performance/vitals`}
        className="inline-flex items-center gap-1.5 text-sm text-[#555A73] hover:text-[#6366F1] transition-colors"
      >
        <ChevronLeft className="w-4 h-4" strokeWidth={1.25} />
        Retour aux resultats
      </Link>

      {/* Page URL header */}
      <div>
        <h1 className="text-xl font-bold text-[#1B2559] break-all">{page.url}</h1>
        <span className="text-xs text-[#8B8FA7] uppercase">{page.pageType}</span>
      </div>

      {/* Score cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <ScoreCard
          label="Perf Mobile"
          score={page.perfScoreMobile}
          icon={<Smartphone className="w-4 h-4 text-[#555A73]" strokeWidth={1.25} />}
        />
        <ScoreCard
          label="Perf Desktop"
          score={page.perfScoreDesktop}
          icon={<Monitor className="w-4 h-4 text-[#555A73]" strokeWidth={1.25} />}
        />
        <ScoreCard
          label="SEO"
          score={page.seoScore}
          icon={<CheckCircle2 className="w-4 h-4 text-[#555A73]" strokeWidth={1.25} />}
        />
        <ScoreCard
          label="Best Practices"
          score={page.bpScore}
          icon={<CheckCircle2 className="w-4 h-4 text-[#555A73]" strokeWidth={1.25} />}
        />
      </div>

      {/* Core Web Vitals */}
      <div>
        <h2 className="text-lg font-bold text-[#1B2559] mb-4">Core Web Vitals</h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <CwvCard label="LCP" hint="Largest Contentful Paint" value={page.lcpMs} unit="s" goodThreshold={2500} poorThreshold={4000} />
          <CwvCard label="FCP" hint="First Contentful Paint" value={page.fcpMs} unit="s" goodThreshold={1800} poorThreshold={3000} />
          <CwvCard label="CLS" hint="Cumulative Layout Shift" value={page.clsValue} unit="" goodThreshold={0.1} poorThreshold={0.25} />
          <CwvCard label="TTFB" hint="Time to First Byte" value={page.ttfbMs} unit="ms" goodThreshold={800} poorThreshold={1800} />
          <CwvCard label="INP" hint="Interaction to Next Paint" value={page.inpMs} unit="ms" goodThreshold={200} poorThreshold={500} />
          <CwvCard label="SI" hint="Speed Index" value={page.siMs} unit="ms" goodThreshold={3400} poorThreshold={5800} />
        </div>
        {page.lcpElement && (
          <div className="mt-3 bg-[#F4F5FA] border border-[#E2E4ED] rounded-xl px-4 py-3">
            <span className="text-xs text-[#8B8FA7] block mb-1">Element LCP</span>
            <code className="text-xs text-[#1B2559] break-all">{page.lcpElement}</code>
          </div>
        )}
      </div>

      {/* Strategy toggle */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setStrategy('mobile')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            strategy === 'mobile'
              ? 'bg-[#6366F1] text-white'
              : 'bg-[#F4F5FA] text-[#555A73] hover:bg-[#E2E4ED]'
          }`}
        >
          <Smartphone className="w-3.5 h-3.5" strokeWidth={1.25} />
          Mobile
        </button>
        <button
          onClick={() => setStrategy('desktop')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            strategy === 'desktop'
              ? 'bg-[#6366F1] text-white'
              : 'bg-[#F4F5FA] text-[#555A73] hover:bg-[#E2E4ED]'
          }`}
        >
          <Monitor className="w-3.5 h-3.5" strokeWidth={1.25} />
          Desktop
        </button>
      </div>

      {/* Opportunities */}
      {opportunities.length > 0 && (
        <div>
          <h2 className="text-lg font-bold text-[#1B2559] mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" strokeWidth={1.25} />
            Opportunites
            <span className="text-sm font-normal text-[#8B8FA7]">({opportunities.length})</span>
          </h2>
          <div className="space-y-2">
            {opportunities.map(item => (
              <AuditItemCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      )}

      {/* Diagnostics */}
      {diagnostics.length > 0 && (
        <div>
          <h2 className="text-lg font-bold text-[#1B2559] mb-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[#6366F1]" strokeWidth={1.25} />
            Diagnostics
            <span className="text-sm font-normal text-[#8B8FA7]">({diagnostics.length})</span>
          </h2>
          <div className="space-y-2">
            {diagnostics.map(item => (
              <AuditItemCard key={item.id} item={item} />
            ))}
          </div>
        </div>
      )}

      {/* Passed */}
      {passed.length > 0 && (
        <div>
          <button
            onClick={() => setShowPassed(v => !v)}
            className="flex items-center gap-2 text-lg font-bold text-[#1B2559] mb-3 hover:opacity-80 transition-opacity"
          >
            <CheckCircle2 className="w-5 h-5 text-emerald-500" strokeWidth={1.25} />
            Audits reussis
            <span className="text-sm font-normal text-[#8B8FA7]">({passed.length})</span>
            {showPassed
              ? <ChevronUp className="w-4 h-4 text-[#8B8FA7]" strokeWidth={1.25} />
              : <ChevronDown className="w-4 h-4 text-[#8B8FA7]" strokeWidth={1.25} />
            }
          </button>
          {showPassed && (
            <div className="space-y-2">
              {passed.map(item => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 bg-white border border-[#E2E4ED] rounded-xl px-5 py-3"
                >
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" strokeWidth={1.25} />
                  <span className="text-sm text-[#1B2559]">{item.title}</span>
                  {item.displayValue && (
                    <span className="text-xs text-[#8B8FA7] ml-auto">{item.displayValue}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
